import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db, retryOperation } from '../config';
import { startOfWeek, endOfWeek, format } from 'date-fns';

const COLLECTION_NAME = 'shifts';

export interface Shift {
  id?: string;
  staffId?: string; // Optional for open shifts
  staffName?: string; // Optional for open shifts
  locationId: string;
  locationName: string;
  date: string; // Format: YYYY-MM-DD
  startTime: string; // Format: HH:mm (24-hour)
  endTime: string; // Format: HH:mm (24-hour)
  duration: number; // Duration in minutes
  breakBeforeHours?: number; // Take break before X hours into shift
  breakDuration?: number; // Break duration in minutes
  breakMandatory?: boolean; // Whether break is mandatory or optional
  hourlyRate?: number;
  position?: string; // e.g., "Marina Manager", "Marina", etc.
  notes?: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  createdBy: string; // User ID who created the shift
}

export interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  hourlyRate?: number;
  positions?: string[]; // Positions they can work
  locationIds?: string[]; // Locations they can work at
  active: boolean;
}

export interface WeeklySchedule {
  weekStart: Date;
  weekEnd: Date;
  shifts: Shift[];
  totalHours: { [staffId: string]: number };
  totalCost: number;
}

/**
 * Create a new shift
 */
export const createShift = async (shiftData: Omit<Shift, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    return await retryOperation(async () => {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...shiftData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    });
  } catch (error) {
    console.error('Error creating shift:', error);
    throw error;
  }
};

/**
 * Get shifts for a specific date range
 */
export const getShiftsByDateRange = async (startDate: Date, endDate: Date): Promise<Shift[]> => {
  try {
    return await retryOperation(async () => {
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      const shiftsQuery = query(
        collection(db, COLLECTION_NAME),
        where('date', '>=', startDateStr),
        where('date', '<=', endDateStr),
        orderBy('date'),
        orderBy('startTime')
      );
      
      const snapshot = await getDocs(shiftsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Shift[];
    });
  } catch (error) {
    console.error('Error getting shifts by date range:', error);
    throw error;
  }
};

/**
 * Get shifts for a specific week
 */
export const getWeeklyShifts = async (weekStartDate: Date): Promise<WeeklySchedule> => {
  try {
    return await retryOperation(async () => {
      const weekStart = startOfWeek(weekStartDate, { weekStartsOn: 1 }); // Monday start
      const weekEnd = endOfWeek(weekStartDate, { weekStartsOn: 1 });
      
      const shifts = await getShiftsByDateRange(weekStart, weekEnd);
      
      // Calculate total hours per staff member
      const totalHours: { [staffId: string]: number } = {};
      let totalCost = 0;
      
      shifts.forEach(shift => {
        if (!totalHours[shift.staffId]) {
          totalHours[shift.staffId] = 0;
        }
        totalHours[shift.staffId] += shift.duration / 60; // Convert minutes to hours
        
        if (shift.hourlyRate) {
          totalCost += (shift.duration / 60) * shift.hourlyRate;
        }
      });
      
      return {
        weekStart,
        weekEnd,
        shifts,
        totalHours,
        totalCost
      };
    });
  } catch (error) {
    console.error('Error getting weekly shifts:', error);
    throw error;
  }
};

/**
 * Update an existing shift
 */
export const updateShift = async (shiftId: string, shiftData: Partial<Shift>): Promise<void> => {
  try {
    await retryOperation(async () => {
      const shiftRef = doc(db, COLLECTION_NAME, shiftId);
      
      // First check if the document exists
      const shiftDoc = await getDoc(shiftRef);
      if (!shiftDoc.exists()) {
        throw new Error(`Shift with ID ${shiftId} does not exist`);
      }
      
      const updateData = {
        ...shiftData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(shiftRef, updateData);
    });
  } catch (error) {
    console.error(`Error updating shift with ID ${shiftId}:`, error);
    throw error;
  }
};

/**
 * Delete a shift
 */
export const deleteShift = async (shiftId: string): Promise<void> => {
  try {
    await retryOperation(async () => {
      await deleteDoc(doc(db, COLLECTION_NAME, shiftId));
    });
  } catch (error) {
    console.error(`Error deleting shift with ID ${shiftId}:`, error);
    throw error;
  }
};

/**
 * Get shifts for a specific staff member
 */
export const getShiftsByStaffId = async (staffId: string, startDate?: Date, endDate?: Date): Promise<Shift[]> => {
  try {
    return await retryOperation(async () => {
      let shiftsQuery = query(
        collection(db, COLLECTION_NAME),
        where('staffId', '==', staffId),
        orderBy('date'),
        orderBy('startTime')
      );
      
      const snapshot = await getDocs(shiftsQuery);
      let shifts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Shift[];
      
      // Filter by date range if provided
      if (startDate && endDate) {
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const endDateStr = format(endDate, 'yyyy-MM-dd');
        shifts = shifts.filter(shift => shift.date >= startDateStr && shift.date <= endDateStr);
      }
      
      return shifts;
    });
  } catch (error) {
    console.error('Error getting shifts by staff ID:', error);
    throw error;
  }
};

/**
 * Bulk create shifts
 */
export const createBulkShifts = async (shifts: Omit<Shift, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<string[]> => {
  try {
    return await retryOperation(async () => {
      const batch = writeBatch(db);
      const shiftIds: string[] = [];
      
      shifts.forEach(shiftData => {
        const docRef = doc(collection(db, COLLECTION_NAME));
        batch.set(docRef, {
          ...shiftData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        shiftIds.push(docRef.id);
      });
      
      await batch.commit();
      return shiftIds;
    });
  } catch (error) {
    console.error('Error creating bulk shifts:', error);
    throw error;
  }
};

/**
 * Calculate shift duration in minutes
 */
export const calculateShiftDuration = (startTime: string, endTime: string): number => {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMinute;
  let endMinutes = endHour * 60 + endMinute;
  
  // Handle overnight shifts
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60; // Add 24 hours
  }
  
  return endMinutes - startMinutes;
};

/**
 * Calculate working hours (total shift minus break time)
 */
export const calculateWorkingHours = (shift: Shift): number => {
  const totalMinutes = shift.duration;
  const breakMinutes = shift.breakDuration || 0;
  return (totalMinutes - breakMinutes) / 60; // Convert to hours
};

/**
 * Calculate when break should be taken (as a time string)
 */
export const calculateBreakTime = (shift: Shift): { breakStart: string; breakEnd: string } | null => {
  if (!shift.breakBeforeHours || !shift.breakDuration) return null;
  
  const [startHour, startMinute] = shift.startTime.split(':').map(Number);
  const shiftStartMinutes = startHour * 60 + startMinute;
  
  // Calculate break start time (X hours into shift)
  const breakStartMinutes = shiftStartMinutes + (shift.breakBeforeHours * 60);
  const breakEndMinutes = breakStartMinutes + shift.breakDuration;
  
  // Convert back to time strings
  const breakStartHour = Math.floor(breakStartMinutes / 60) % 24;
  const breakStartMin = breakStartMinutes % 60;
  const breakEndHour = Math.floor(breakEndMinutes / 60) % 24;
  const breakEndMin = breakEndMinutes % 60;
  
  const breakStart = `${breakStartHour.toString().padStart(2, '0')}:${breakStartMin.toString().padStart(2, '0')}`;
  const breakEnd = `${breakEndHour.toString().padStart(2, '0')}:${breakEndMin.toString().padStart(2, '0')}`;
  
  return { breakStart, breakEnd };
};

/**
 * Format break info for display
 */
export const formatBreakInfo = (shift: Shift): string => {
  if (!shift.breakBeforeHours || !shift.breakDuration) return '';
  
  const breakTime = calculateBreakTime(shift);
  if (!breakTime) return '';
  
  const mandatory = shift.breakMandatory ? 'Required' : 'Optional';
  const duration = shift.breakDuration >= 60 
    ? `${Math.floor(shift.breakDuration / 60)}h ${shift.breakDuration % 60}m`.replace(' 0m', '')
    : `${shift.breakDuration}m`;
  
  return `${mandatory} ${duration} break before ${formatTimeForDisplay(breakTime.breakStart)}`;
};

/**
 * Format time for display (convert 24-hour to 12-hour)
 */
export const formatTimeForDisplay = (time: string): string => {
  const [hour, minute] = time.split(':').map(Number);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

/**
 * Get daily schedule totals (working hours, excluding breaks)
 */
export const getDailyTotals = (shifts: Shift[], date: string): { hours: number; cost: number } => {
  const dayShifts = shifts.filter(shift => shift.date === date);
  
  let totalWorkingHours = 0;
  let totalCost = 0;
  
  dayShifts.forEach(shift => {
    const workingHours = calculateWorkingHours(shift);
    totalWorkingHours += workingHours;
    if (shift.hourlyRate) {
      totalCost += workingHours * shift.hourlyRate;
    }
  });
  
  return { hours: totalWorkingHours, cost: totalCost };
}; 